import { Loader2, RefreshCw, ShieldAlert, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchMarketRegime, fetchScanner, fetchScannerSignalStats, fetchScanStatus, triggerRefresh } from '../api';
import { MarketRegime, ScannerResponse, ScannerSignalStats, SigAgg, SigGroup } from '../types';
import { isOwner } from '../owner';
import GuideCard from './GuideCard';
import ScannerTable from './ScannerTable';
import { sigLabel } from '../interpret';   // 📖 화면 이름 사전(TERMS.md) — 값(필터 키)은 그대로, 글자만 바꿈
import SectorSidebar, { GROUPS } from './SectorSidebar';

// 🎨 규격 V2(08-23) 공용 부품 — 버튼은 고스트/켜짐 두 가지, 칩은 중립/링크/경고 세 가지.
//   폰에선 한 칸 낮춰(26px) 필터 줄이 표를 밀어내지 않게 한다.
const BTN = 'inline-flex items-center gap-1.5 h-[26px] px-2 sm:h-[29px] sm:px-[11px] rounded-lg border text-[11.5px] sm:text-xs transition-colors';
const GHOST = `${BTN} border-[#202A3C] text-dc-ink2/90 font-medium hover:border-[#33405A] hover:text-dc-ink1 disabled:opacity-40 disabled:hover:border-[#202A3C]`;
const ON = `${BTN} border-dc-point/35 bg-dc-point/10 text-dc-point font-semibold`;
const KICKER = 'text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3';

// 신호 포워드테스트 성적표 — 강한 신호 종목의 1·2·3·4·5·10·20일 수익률 매트릭스(며칠째 잘 가나·점수 변별력 검증).
function SignalReport({ stats }: { stats: ScannerSignalStats | null }) {
  if (!stats) return <p className="text-dc-ink3 text-xs py-4 text-center">불러오는 중...</p>;
  const H = stats.horizons || [1, 2, 3, 4, 5, 10, 20];
  // 수익률은 한국식 색 관례 — 오르면 웜, 내리면 쿨(옛 초록/빨강은 뜻이 뒤집혀 있었다).
  const avgCls = (v: number | null | undefined) => (v == null ? 'text-dc-ink3/60' : v > 0 ? 'text-dc-up' : v < 0 ? 'text-dc-down' : 'text-dc-ink2');
  const Cell = ({ a }: { a?: SigAgg }) =>
    !a || a.n === 0
      ? <td className="text-center text-dc-ink3/60">─</td>
      : <td className={`text-center ${avgCls(a.avg)}`}
          title={`승률 ${a.win_rate}% · ${a.n}건${a.avg_excess != null ? ` · 코스피대비 초과 ${a.avg_excess > 0 ? '+' : ''}${a.avg_excess}%·이긴비율 ${a.beat_rate}%(${a.excess_n})` : ''}`}>
          {a.avg != null && a.avg > 0 ? '+' : ''}{a.avg}%{a.avg_excess != null && a.avg_excess > 0 && a.avg != null && a.avg < 0 ? <span className="text-dc-point/80" title="종목은 마이너스지만 같은 기간 코스피보다는 덜 빠짐(초과수익 +)">*</span> : null}</td>;
  const Row = ({ label, g, hi }: { label: string; g?: SigGroup; hi?: boolean }) => (
    <tr className={hi ? 'bg-white/[0.015]' : ''}>
      <td className="text-dc-ink2 py-1 pl-1.5 whitespace-nowrap">{label}</td>
      <td className="text-dc-ink3 text-center">{g?.observed ?? 0}</td>
      {H.map((h) => <Cell key={h} a={g?.ret?.[String(h)]} />)}
    </tr>
  );
  const span = 2 + H.length;
  return (
    <div className="text-[11.5px] space-y-2">
      <p className="text-dc-ink3 leading-snug">
        강한 신호(<b className="text-dc-ink2">추세 초기·매우 강함·강함</b>) 종목을 자동으로 찍어 <b>1~20일 수익률</b>을 쌓는 포워드테스트.
        총 <b className="text-dc-ink1">{stats.n}</b>건 관찰(대기 {stats.open}).
        <span className="text-dc-ink3/80"> 결과는 해당 일수 지나야 채워짐 · 표본 쌓여야 의미.</span>
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-[11.5px] tabular-nums">
          <thead>
            <tr className={`${KICKER} border-b border-dc-hair`}>
              <th className="text-left pl-1.5 py-1.5 font-[650]">구분</th>
              <th className="px-1 font-[650]">관찰</th>
              {H.map((h) => <th key={h} className="px-1.5 font-[650]">{h}일</th>)}
            </tr>
          </thead>
          <tbody>
            <tr><td colSpan={span} className={`${KICKER} pt-2 pl-1.5`}>신호별</td></tr>
            <Row label="초기" g={stats.by_signal['신규진입']} hi />
            <Row label="매우 강함" g={stats.by_signal['완벽한추세']} />
            <Row label="강함" g={stats.by_signal['강한추세']} />
            <tr><td colSpan={span} className={`${KICKER} pt-2.5 pl-1.5`}>점수대별 — 점수 높을수록 잘 가나?</td></tr>
            <Row label="90점+" g={stats.by_score['90+']} hi />
            <Row label="80~89" g={stats.by_score['80~89']} />
            <Row label="70~79" g={stats.by_score['70~79']} />
            <Row label="70 미만" g={stats.by_score['<70']} />
            <tr><td colSpan={span} className={`${KICKER} pt-2.5 pl-1.5 text-dc-ink2`}>전체</td></tr>
            <Row label="전체" g={{ observed: stats.n, ret: stats.overall.ret }} />
          </tbody>
        </table>
      </div>
      <p className="text-dc-ink3/80 text-[10.5px] leading-snug">숫자 = 평균수익%(마우스 올리면 승률·건수). <b>1일→20일 흐름</b>으로 "며칠째 가장 잘 가나/식나"를 봅니다. ─ 는 아직 그 일수가 안 지난 것.</p>
    </div>
  );
}

const SIGNAL_FILTERS: Record<string, string[]> = {
  minervini: ['전체', '신규진입', '완벽한추세', '강한추세', '추세형성', '단기추세', '관망', '위험'],
  reversion: ['전체', '반등임박', '과매도', '중립', '약세이탈'],
};

// 🎨 규격 V2(08-23) — 국면 배너는 색 칠한 띠가 아니라 **헤어라인 줄**. 판정은 앞의 점 하나로만 말한다.
//   상태 3단(전 화면 공통): 양호=ok / 주의=앰버 / 위험=warn / 미상=희미.
//   down(파랑)은 방향(하락) 전용이라 '위험'에 쓰지 않는다 — 쓰면 위험이 주의보다 순해 보인다.
const REGIME_DOT: Record<string, string> = {
  '양호': 'bg-dc-ok',
  '주의': 'bg-dc-point',
  '위험': 'bg-dc-warn',
  '미상': 'bg-dc-ink3',
};

// ① 시그널 등급 순위 — 지난 방문 대비 승격/강등 판정용(높을수록 강함)
const SIG_RANK: Record<string, number> = { '신규진입': 6, '완벽한추세': 5, '강한추세': 4, '추세형성': 3, '단기추세': 2, '관망': 1, '위험': 0 };

function Dot({ regime }: { regime: string }) {
  return <span className={`w-[7px] h-[7px] rounded-full shrink-0 ${REGIME_DOT[regime] ?? REGIME_DOT['미상']}`} />;
}

function RegimeBanner({ regime }: { regime: MarketRegime }) {
  const kr = regime.kr ?? { overall: regime.overall, message: regime.message };
  const us = regime.us;
  return (
    <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5 px-4 py-2.5 border-b border-dc-hair bg-dc-point/[0.03] text-xs text-dc-ink2">
      <ShieldAlert size={13} className="shrink-0 text-dc-ink3" />
      <span className="flex items-center gap-1.5 whitespace-nowrap">
        <Dot regime={kr.overall} />
        <span className="font-semibold text-dc-ink1">🇰🇷 한국 {kr.overall}</span>
        <span className="text-dc-ink3 hidden md:inline">· {kr.message}</span>
      </span>
      {us && (
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <Dot regime={us.overall} />
          <span className="font-semibold text-dc-ink1">🇺🇸 미국 {us.overall}</span>
          <span className="text-dc-ink3 hidden lg:inline">
            (S&P {us.sp500.regime} · NASDAQ {us.nasdaq.regime})
          </span>
        </span>
      )}
      {regime.macro && (
        <span className="flex items-center gap-1.5 whitespace-nowrap" title={regime.macro.reasons.join(', ')}>
          <span>매크로 {regime.macro.level}</span>
          {regime.macro.vix != null && (
            <span className="text-dc-ink3">VIX {regime.macro.vix}</span>
          )}
          {regime.macro.us10y != null && (
            <span className="text-dc-ink3 hidden lg:inline">· 10Y {regime.macro.us10y}%</span>
          )}
        </span>
      )}
      {regime.events && regime.events.length > 0 && (
        <span className="flex flex-wrap items-center gap-1.5">
          {regime.events.map((e) => (
            <span
              key={e.name + e.date}
              className="inline-flex items-center h-5 px-2 rounded-md border border-dc-warn/40 bg-dc-warn/[0.08] text-dc-warn text-[11.5px] font-semibold whitespace-nowrap"
              title="주요 이벤트 전후 변동성 주의 — 신규 진입 신중"
            >
              ⚠ {e.name} {e.d_day === 0 ? '오늘' : `D-${e.d_day}`}
            </span>
          ))}
        </span>
      )}
      {regime.kr?.distribution_days != null && (
        <span
          className={`inline-flex items-center h-5 px-2 rounded-md border text-[11.5px] font-semibold whitespace-nowrap ${
            regime.kr.distribution_days >= 5
              ? 'border-dc-warn/40 bg-dc-warn/[0.08] text-dc-warn'
              : 'border-dc-line1 bg-dc-bg1 text-dc-ink3'
          }`}
          title="분산일 — 지수가 거래량 증가 속 하락한 날 (25일 중). 5일+면 기관 매도 경계"
        >
          분산일 {regime.kr.distribution_days}
        </span>
      )}
    </div>
  );
}

interface Props {
  onSelectTicker: (ticker: string, name: string) => void;
}

export default function FullScanPage({ onSelectTicker }: Props) {
  const [sector, setSector] = useState('전체');
  const [signal, setSignal] = useState('전체');
  const [strategy, setStrategy] = useState<'minervini' | 'reversion'>('minervini');
  const [data, setData] = useState<ScannerResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [regime, setRegime] = useState<MarketRegime | null>(null);
  const [showStats, setShowStats] = useState(false);                       // 신호 성적표 패널
  const [sigStats, setSigStats] = useState<ScannerSignalStats | null>(null);
  const [changeFilter, setChangeFilter] = useState<'all' | 'new' | 'up' | 'down'>('all');   // ① 변화 필터
  const [delta, setDelta] = useState<{ newS: Set<string>; up: Set<string>; down: Set<string> } | null>(null);
  useEffect(() => { fetchScannerSignalStats().then(setSigStats).catch(() => {}); }, []);   // ② 시그널 성적표(꼬리표용)
  // ① 지난 방문 대비 시그널 변화 — 기기에 마지막 본 시그널 저장·비교(첫 방문은 baseline만).
  useEffect(() => {
    const items = data?.items;
    if (!items || !items.length || strategy !== 'minervini') { setDelta(null); return; }
    let prev: Record<string, string> = {};
    try { prev = JSON.parse(localStorage.getItem('scan_last_sig') || '{}'); } catch { /* noop */ }
    const hadPrev = Object.keys(prev).length > 0;
    const newS = new Set<string>(), up = new Set<string>(), down = new Set<string>(), cur: Record<string, string> = {};
    for (const it of items) {
      const sig = it.signal; cur[it.ticker] = sig;
      const old = prev[it.ticker];
      if (old === undefined) { if (sig === '신규진입') newS.add(it.ticker); continue; }
      const ro = SIG_RANK[old] ?? 1, rn = SIG_RANK[sig] ?? 1;
      if (sig === '신규진입' && old !== '신규진입') newS.add(it.ticker);
      else if (rn > ro) up.add(it.ticker);
      else if (rn < ro) down.add(it.ticker);
    }
    try { localStorage.setItem('scan_last_sig', JSON.stringify(cur)); } catch { /* noop */ }
    setDelta(hadPrev ? { newS, up, down } : null);
  }, [data, strategy]);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const openStats = () => {
    setShowStats((v) => !v);
    if (!showStats) fetchScannerSignalStats().then(setSigStats).catch(() => {});
  };

  const loadRegime = useCallback(() => {
    fetchMarketRegime().then(setRegime).catch(() => {});
  }, []);

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const loadFiltered = useCallback(async (sec: string, sig: string, strat: string) => {
    setLoading(true);
    setError('');
    try {
      const result = await fetchScanner({ sector: sec, signal: sig, limit: 200, strategy: strat });
      setData(result);
    } catch {
      setError('데이터 로딩 실패. 백엔드 서버를 확인하세요.');
    } finally {
      setLoading(false);
    }
  }, []);

  const startPolling = useCallback((sec: string, sig: string, strat: string) => {
    stopPoll();
    setScanning(true);
    pollRef.current = setInterval(async () => {
      try {
        const status = await fetchScanStatus();
        if (!status.scanning && status.cached_count > 0) {
          stopPoll();
          setScanning(false);
          await loadFiltered(sec, sig, strat);
        }
      } catch { /* silent */ }
    }, 3000);
  }, [loadFiltered]);

  const initialize = useCallback(async (sec: string, sig: string, strat: string) => {
    setError('');
    try {
      const status = await fetchScanStatus();
      if (!status.stale && status.cached_count > 0) {
        await loadFiltered(sec, sig, strat);
      } else if (status.scanning) {
        startPolling(sec, sig, strat);
      } else {
        setScanning(true);
        await triggerRefresh();
        startPolling(sec, sig, strat);
      }
    } catch {
      setLoading(true);
      try {
        const result = await fetchScanner({ sector: sec, signal: sig, limit: 200, strategy: strat });
        setData(result);
      } catch {
        setError('데이터 로딩 실패.');
      } finally {
        setLoading(false);
      }
    }
  }, [loadFiltered, startPolling]);

  useEffect(() => {
    initialize(sector, signal, strategy);
    loadRegime();
    return stopPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data !== null) loadFiltered(sector, signal, strategy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sector, signal, strategy]);

  const handleRefresh = async (force = false) => {
    stopPoll();
    setScanning(true);
    setData(null);
    try {
      await triggerRefresh(force);
      startPolling(sector, signal, strategy);
      loadRegime();
    } catch {
      setError('갱신 요청 실패');
      setScanning(false);
    }
  };

  const changeStrategy = (s: 'minervini' | 'reversion') => {
    setStrategy(s);
    setSignal('전체');   // 전략 바뀌면 시그널 필터 리셋
  };

  const isLoading = loading || scanning;
  // ① 변화 필터 적용
  const shownItems = (() => {
    const items = data?.items ?? [];
    if (!delta || changeFilter === 'all') return items;
    const set = changeFilter === 'new' ? delta.newS : changeFilter === 'up' ? delta.up : delta.down;
    return items.filter((it) => set.has(it.ticker));
  })();
  const chip = (on: boolean) => `inline-flex items-center h-[22px] px-2 rounded-md border text-[11.5px] transition-colors ${on ? 'border-dc-point/35 bg-dc-point/10 text-dc-point font-semibold' : 'border-[#1F2938] bg-dc-bg1/60 text-dc-ink2/90 hover:text-dc-ink1'}`;

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      <SectorSidebar active={sector} onChange={setSector} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* 컨트롤 바 */}
        <div className="px-4 sm:px-[18px] py-3 border-b border-dc-hair flex flex-wrap items-center gap-2">
          {/* 분류 선택 — 폰 전용(사이드바가 md 미만에서 숨겨지므로 대체). 네이티브 select = 폰 피커 */}
          <select
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            className="md:hidden bg-dc-bg1 border border-[#202A3C] rounded-lg h-[26px] sm:h-[29px] py-0 px-2 text-[11.5px] sm:text-xs text-dc-ink2 outline-none focus:border-dc-point/50"
            aria-label="분류 선택"
          >
            <option value="전체">전체 분류</option>
            {GROUPS.map((g) =>
              g.items.length > 0 ? (
                <optgroup key={g.label} label={g.label}>
                  {g.value && <option value={g.value}>{g.label} 전체</option>}
                  {g.items.map((it) => (
                    <option key={it} value={it}>{it}</option>
                  ))}
                </optgroup>
              ) : (
                <option key={g.label} value={g.value}>{g.label}</option>
              )
            )}
          </select>

          <span className="text-dc-ink2 text-[13px] whitespace-nowrap">
            스캔 종목{' '}
            <span className="text-dc-ink1 font-bold">
              {data ? data.total.toLocaleString('ko-KR') : '─'}
            </span>
            개
          </span>

          <span className="w-px h-4 bg-dc-line1 mx-1 hidden sm:block" />

          {/* 전략 토글 — 파란 채움 세그먼트 은퇴, 고스트/켜짐 버튼 두 개로(규격 V2) */}
          <div className="flex flex-wrap gap-2">
            {([['minervini', '추세 (Minervini)'], ['reversion', '역추세 (반등)']] as const).map(([k, label]) => (
              <button key={k} onClick={() => changeStrategy(k)} className={strategy === k ? ON : GHOST}>
                {label}
              </button>
            ))}
          </div>

          <span className="w-px h-4 bg-dc-line1 mx-1 hidden sm:block" />

          <div className="flex flex-wrap gap-2">
            {SIGNAL_FILTERS[strategy].map((s) => {
              // ④ⓐ 시그널 신뢰도 꼬리표를 '시그널 단위'인 필터 칩에 붙임(행마다 반복돼 오해되던 것 수리)
              const st = strategy === 'minervini' ? sigStats?.by_signal?.[s] : undefined;
              let tag = '';
              if (st) {
                const h = ['5', '3', '2', '1'].find((k) => (st.ret[k]?.n ?? 0) >= 5);
                if (h && st.ret[h].avg != null) tag = ` (+${h}일 ${st.ret[h].avg! >= 0 ? '+' : ''}${st.ret[h].avg}%)`;
                else if ((st.observed ?? 0) >= 1) tag = ' (수집중)';
              }
              return (
                <button
                  key={s}
                  onClick={() => setSignal(s)}
                  title={tag ? `이 시그널 최근 성적${tag}` : undefined}
                  className={signal === s ? ON : GHOST}
                >
                  {sigLabel(s)}{tag && <span className={`text-[10.5px] ${signal === s ? 'text-dc-point/80' : 'text-dc-ink3'}`}>{tag.trim()}</span>}
                </button>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {data?.scan_time && (
              <span className="text-dc-ink3 text-[11.5px]">
                {new Date(data.scan_time).toLocaleTimeString('ko-KR')}
              </span>
            )}
            {isOwner() && (
              <button
                onClick={openStats}
                title="강한 신호 종목이 실제로 +1~20일 뒤 올랐는지 포워드테스트 누적 성적(관리자 전용)"
                className={showStats ? ON : GHOST}
              >
                📊 신호 성적표
              </button>
            )}
            <button onClick={() => handleRefresh(false)} disabled={isLoading} className={GHOST}>
              <RefreshCw size={12} className={isLoading ? 'animate-spin' : ''} />
              재스캔
            </button>
            <button
              onClick={() => handleRefresh(true)}
              disabled={isLoading}
              title="OHLCV 캐시를 비우고 전량 새로 다운로드 (느림)"
              className={GHOST}
            >
              강제
            </button>
          </div>
        </div>

        {/* 신호 포워드테스트 성적표 (관리자 전용) */}
        {showStats && isOwner() && (
          <div className="mx-4 my-3 rounded-[10px] border border-dc-line1 bg-dc-bg1/50 px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-dc-ink1 text-sm font-bold">📊 신호 성적표 (포워드테스트)</span>
              <button onClick={() => sigStats && fetchScannerSignalStats().then(setSigStats).catch(() => {})}
                className="text-dc-ink3 hover:text-dc-ink1"><RefreshCw size={12} /></button>
              <button onClick={() => setShowStats(false)} className="ml-auto text-dc-ink3 hover:text-dc-ink1"><X size={14} /></button>
            </div>
            <SignalReport stats={sigStats} />
          </div>
        )}

        {/* 시장 국면 배너 */}
        {regime && <RegimeBanner regime={regime} />}

        {/* ③ 국면 렌즈 넛지 — 약세장엔 추세 신호가 마르니 역추세(반등) 렌즈를 권함(자동전환 아님) */}
        {regime && regime.overall !== '양호' && strategy === 'minervini' && (
          <div className="mx-4 mt-3 rounded-[10px] border border-dc-point/30 bg-dc-point/[0.05] px-3 py-2 text-[11.5px] flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
            <span className="text-dc-point font-semibold">📉 지금은 추세 신호가 마르는 장</span>
            <span className="text-dc-ink2">추세 종목이 적은 게 정상 — 반등(역추세) 쪽으로 보세요</span>
            <button onClick={() => changeStrategy('reversion')} className={`ml-auto ${ON}`}>역추세로 전환</button>
          </div>
        )}

        {/* 스캔 진행 배너 */}
        {scanning && (
          <div className="flex items-center justify-center gap-2 py-3 border-b border-dc-hair bg-dc-bg0/60">
            <Loader2 size={14} className="animate-spin text-dc-point" />
            <span className="text-dc-ink2 text-xs">전종목 분석 중... (2~3분 소요)</span>
          </div>
        )}

        {error && !isLoading && (
          <div className="px-4 py-3 border-b border-dc-warn/30 bg-dc-warn/[0.07] text-dc-warn text-xs">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-auto">
          <GuideCard id="fullscan" title="전체 스캔 사용법">
            <p className="mb-1.5"><b className="text-dc-ink1">이 화면은?</b> KOSPI·KOSDAQ·S&amp;P500 <b>전체</b>에서 추세 강한 종목을 발굴해요.</p>
            <p className="mb-1.5"><b className="text-dc-ink1">점수</b>는 추세 강도(높을수록 강함), <b className="text-dc-ink1">RS</b>는 전 종목 중 상대강도 순위예요.</p>
            <p><b className="text-dc-ink1">이렇게 쓰세요:</b> ① 점수·RS·시그널로 정렬해 강한 종목 찾기 → ② 좌측 섹터·상단 시그널 필터로 좁히기 → ③ 종목 클릭 → AI 심층분석 → ④ 좋은 건 <b>관심종목 탭</b>에 추가해 추적.</p>
          </GuideCard>
          {/* ① 지난 방문 대비 변화 — 매일 오는 이유(랭킹 아닌 변화). 클릭하면 그 종목만. */}
          {delta && (delta.newS.size + delta.up.size + delta.down.size > 0) && (
            <div className="mx-4 mt-3 flex flex-wrap items-center gap-2 text-[11.5px]">
              <span className="text-dc-ink3">지난 방문 대비</span>
              {delta.newS.size > 0 && <button onClick={() => setChangeFilter(changeFilter === 'new' ? 'all' : 'new')} className={chip(changeFilter === 'new')}>🆕 추세 초기 {delta.newS.size}</button>}
              {delta.up.size > 0 && <button onClick={() => setChangeFilter(changeFilter === 'up' ? 'all' : 'up')} className={chip(changeFilter === 'up')}>⬆️ 좋아짐 {delta.up.size}</button>}
              {delta.down.size > 0 && <button onClick={() => setChangeFilter(changeFilter === 'down' ? 'all' : 'down')} className={chip(changeFilter === 'down')}>⬇️ 나빠짐 {delta.down.size}</button>}
              {changeFilter !== 'all' && <button onClick={() => setChangeFilter('all')} className="text-dc-ink3 underline hover:text-dc-ink1">전체 보기</button>}
            </div>
          )}
          <ScannerTable
            data={shownItems}
            loading={loading}
            strategy={strategy}
            sigStats={sigStats}
            onRowClick={(item) => onSelectTicker(item.ticker, item.name)}
          />
        </div>
      </div>
    </div>
  );
}
